import { GameState, GameEvent, Ability, AbilityType, CardDef, PlayerSide } from '../types.js';
import { getPlayerState, getOpponentSide, getInstance, getEffectivePower, adjustClock } from './GameState.js';
import { drawCards, shuffle } from './DeckUtils.js';
import { nextInt } from './PRNG.js';
import { getConfig } from '../config.js';
import { applyDamage } from './CombatResolver.js';
import { VERB_HANDLERS, koCharacter, returnToHand } from './Verbs.js';
import { selectTargets } from './Targeting.js';
import { isVerb } from '../shared/targeting.js';
import { logCardLink } from '../shared/log-markers.js';
import { narrateAbilityEvents } from './LogNarration.js';

type EffectHandler = (
  state: GameState,
  sourceInstanceId: string,
  catalog: Map<string, CardDef>,
  intensity: number,
) => GameEvent[];

const EFFECT_HANDLERS: Record<string, EffectHandler> = {
  'draw': (state, sourceId, _catalog, intensity) => {
    const inst = getInstance(state, sourceId);
    const ps = getPlayerState(state, inst.owner);
    const events: GameEvent[] = [];
    const count = Math.min(intensity, ps.deck.length);
    if (count > 0) {
      const { drawn, remaining } = drawCards(ps.deck, count);
      ps.deck = remaining;
      ps.hand.push(...drawn);
      for (const id of drawn) {
        state.cardInstances.get(id)!.zone = 'hand';
        events.push({ type: 'card-drawn', instanceId: id, player: inst.owner });
      }
    }
    return events;
  },

  'steal-spirit': (state, sourceId, _catalog, intensity) => {
    const inst = getInstance(state, sourceId);
    const ps = getPlayerState(state, inst.owner);
    const opSide = getOpponentSide(inst.owner);
    const ops = getPlayerState(state, opSide);
    const stolen = Math.min(intensity, ops.spirit);
    const events: GameEvent[] = [];
    if (stolen > 0) {
      const oldOpSpirit = ops.spirit;
      ops.spirit -= stolen;
      events.push({ type: 'spirit-changed', player: opSide, oldSpirit: oldOpSpirit, newSpirit: ops.spirit });
    }
    const oldSpirit = ps.spirit;
    ps.spirit += stolen;
    events.push({ type: 'spirit-changed', player: inst.owner, oldSpirit, newSpirit: ps.spirit });
    return events;
  },

  'damage-opponent': (state, sourceId, _catalog, intensity) => {
    const inst = getInstance(state, sourceId);
    const opSide = getOpponentSide(inst.owner);
    const ops = getPlayerState(state, opSide);
    const oldHp = ops.hp;
    ops.hp -= intensity;
    const events: GameEvent[] = [{ type: 'hp-changed', player: opSide, oldHp, newHp: ops.hp, amount: -intensity }];
    // Emit game-over here so the responsibleCardId points at the
    // ability's owner. The outer dispatch HP check also emits a
    // game-over, but it only sees the top-level action — which for
    // on-attack triggers is DECLARE_ATTACKERS (no instanceId) and
    // would produce a null attribution. Setting `state.winner` here
    // gates the outer check so we don't double-emit.
    if (ops.hp <= 0 && state.winner === null) {
      state.winner = inst.owner;
      state.winReason = 'hp';
      events.push({ type: 'game-over', winner: inst.owner, reason: 'hp', responsibleCardId: inst.cardId });
    }
    return events;
  },

  'heal': (state, sourceId, _catalog, intensity) => {
    const inst = getInstance(state, sourceId);
    const ps = getPlayerState(state, inst.owner);
    const oldHp = ps.hp;
    ps.hp = Math.min(ps.hp + intensity, state.maxHp);
    return [{ type: 'hp-changed', player: inst.owner, oldHp, newHp: ps.hp, amount: ps.hp - oldHp }];
  },

  'spirit-gain': (state, sourceId, _catalog, intensity) => {
    const inst = getInstance(state, sourceId);
    const ps = getPlayerState(state, inst.owner);
    const oldSpirit = ps.spirit;
    ps.spirit += intensity;
    return [{ type: 'spirit-changed', player: inst.owner, oldSpirit, newSpirit: ps.spirit }];
  },

  'draw-and-spirit': (state, sourceId, _catalog, intensity) => {
    const inst = getInstance(state, sourceId);
    const ps = getPlayerState(state, inst.owner);
    const events: GameEvent[] = [];
    const count = Math.min(intensity, ps.deck.length);
    if (count > 0) {
      const { drawn, remaining } = drawCards(ps.deck, count);
      ps.deck = remaining;
      ps.hand.push(...drawn);
      for (const id of drawn) {
        state.cardInstances.get(id)!.zone = 'hand';
        events.push({ type: 'card-drawn', instanceId: id, player: inst.owner });
      }
    }
    const oldSpirit = ps.spirit;
    ps.spirit += intensity;
    events.push({ type: 'spirit-changed', player: inst.owner, oldSpirit, newSpirit: ps.spirit });
    return events;
  },

  'grant-extra-rally': (state, sourceId, _catalog, intensity) => {
    const inst = getInstance(state, sourceId);
    const ps = getPlayerState(state, inst.owner);
    ps.bonusRallies += intensity;
    return [];
  },

  'grant-extra-support': (state, sourceId, _catalog, intensity) => {
    const inst = getInstance(state, sourceId);
    const ps = getPlayerState(state, inst.owner);
    ps.bonusSupports += intensity;
    return [];
  },

  // Passive effects — these are checked during power calculation or support, not triggered directly
  'passive-boost-allies': () => [],
  'passive-intimidate': () => [],
  'passive-support-bonus': () => [],
  'passive-extra-support': () => [],
  'return-exchange': (state, sourceId, catalog, _intensity) => {
    const inst = getInstance(state, sourceId);
    const ps = getPlayerState(state, inst.owner);
    const opSide = getOpponentSide(inst.owner);
    const ops = getPlayerState(state, opSide);
    const events: GameEvent[] = [];

    // Find weakest ally other than self
    let weakestAlly: string | null = null;
    let lowestPower = Infinity;
    for (const slot of ps.ring) {
      if (!slot.instanceId || slot.instanceId === sourceId) continue;
      const ep = getEffectivePower(state, slot.instanceId, catalog);
      if (ep < lowestPower) {
        lowestPower = ep;
        weakestAlly = slot.instanceId;
      }
    }

    if (!weakestAlly) return [];

    // Return weakest ally
    returnToHand(state, weakestAlly, catalog, events);

    // Find and return opponent's strongest
    let strongestEnemy: string | null = null;
    let highestPower = -1;
    for (const slot of ops.ring) {
      if (!slot.instanceId) continue;
      const ep = getEffectivePower(state, slot.instanceId, catalog);
      if (ep > highestPower) {
        highestPower = ep;
        strongestEnemy = slot.instanceId;
      }
    }

    if (strongestEnemy) {
      returnToHand(state, strongestEnemy, catalog, events);
    }

    return events;
  },

  'drain-spirit': (state, sourceId, _catalog, intensity) => {
    const inst = getInstance(state, sourceId);
    const opSide = getOpponentSide(inst.owner);
    const ops = getPlayerState(state, opSide);
    const drained = Math.min(intensity, ops.spirit);
    if (drained > 0) {
      const oldSpirit = ops.spirit;
      ops.spirit -= drained;
      return [{ type: 'spirit-changed', player: opSide, oldSpirit, newSpirit: ops.spirit }];
    }
    return [];
  },

  'copy-power': (state, sourceId, catalog, _intensity) => {
    const inst = getInstance(state, sourceId);
    const opSide = getOpponentSide(inst.owner);
    const ops = getPlayerState(state, opSide);
    let highestPower = 0;
    for (const slot of ops.ring) {
      if (!slot.instanceId) continue;
      const ep = getEffectivePower(state, slot.instanceId, catalog);
      if (ep > highestPower) highestPower = ep;
    }
    if (highestPower > 0) {
      const oldPower = inst.currentPower;
      inst.currentPower = highestPower;
      return [{ type: 'power-changed', instanceId: sourceId, oldPower, newPower: inst.currentPower, amount: highestPower - oldPower }];
    }
    return [];
  },

  'steal-power': (state, sourceId, catalog, _intensity) => {
    const inst = getInstance(state, sourceId);
    const ps = getPlayerState(state, inst.owner);
    const events: GameEvent[] = [];
    let weakest: string | null = null;
    let lowestPower = Infinity;
    for (const slot of ps.ring) {
      if (!slot.instanceId || slot.instanceId === sourceId) continue;
      const ep = getEffectivePower(state, slot.instanceId, catalog);
      if (ep < lowestPower) {
        lowestPower = ep;
        weakest = slot.instanceId;
      }
    }
    if (weakest) {
      const target = getInstance(state, weakest);
      const stolenPower = target.currentPower;
      koCharacter(state, weakest, events, catalog);
      const oldPower = inst.currentPower;
      inst.currentPower += stolenPower;
      events.push({ type: 'power-changed', instanceId: sourceId, oldPower, newPower: inst.currentPower, amount: stolenPower });
    }
    return events;
  },

  'sacrifice-heal': (state, sourceId, catalog, intensity) => {
    const inst = getInstance(state, sourceId);
    const ps = getPlayerState(state, inst.owner);
    const events: GameEvent[] = [];
    let weakest: string | null = null;
    let lowestPower = Infinity;
    for (const slot of ps.ring) {
      if (!slot.instanceId || slot.instanceId === sourceId) continue;
      const ep = getEffectivePower(state, slot.instanceId, catalog);
      if (ep < lowestPower) {
        lowestPower = ep;
        weakest = slot.instanceId;
      }
    }
    if (weakest) {
      koCharacter(state, weakest, events, catalog);
      const oldHp = ps.hp;
      ps.hp = Math.min(ps.hp + intensity, state.maxHp);
      events.push({ type: 'hp-changed', player: inst.owner, oldHp, newHp: ps.hp, amount: ps.hp - oldHp });
    }
    return events;
  },

  'discard-enemy-hand': (state, sourceId, _catalog, intensity) => {
    const inst = getInstance(state, sourceId);
    const opSide = getOpponentSide(inst.owner);
    const ops = getPlayerState(state, opSide);
    const events: GameEvent[] = [];
    const count = Math.min(intensity, ops.hand.length);
    for (let i = 0; i < count; i++) {
      if (ops.hand.length === 0) break;
      const idx = nextInt(state.rng, ops.hand.length);
      const discardedId = ops.hand[idx];
      ops.hand.splice(idx, 1);
      const discardedInst = state.cardInstances.get(discardedId);
      if (discardedInst) {
        discardedInst.zone = 'discard';
        ops.discardPile.push(discardedId);
        events.push({ type: 'card-discarded', instanceId: discardedId, player: opSide });
      }
    }
    return events;
  },

  'mill-enemy': (state, sourceId, _catalog, intensity) => {
    const inst = getInstance(state, sourceId);
    const opSide = getOpponentSide(inst.owner);
    const ops = getPlayerState(state, opSide);
    const events: GameEvent[] = [];
    const count = Math.min(intensity, ops.deck.length);
    for (let i = 0; i < count; i++) {
      const milledId = ops.deck.shift()!;
      const milledInst = state.cardInstances.get(milledId);
      if (milledInst) {
        milledInst.zone = 'discard';
        ops.discardPile.push(milledId);
        events.push({ type: 'card-milled', instanceId: milledId, player: opSide });
      }
    }
    return events;
  },

  'recycle-hand': (state, sourceId, _catalog, intensity) => {
    const inst = getInstance(state, sourceId);
    const ps = getPlayerState(state, inst.owner);
    const events: GameEvent[] = [];
    // Shuffle up to intensity cards from discard back into deck
    const count = Math.min(intensity, ps.discardPile.length);
    for (let i = 0; i < count; i++) {
      const recycledId = ps.discardPile.pop()!;
      const recycledInst = state.cardInstances.get(recycledId);
      if (recycledInst) {
        recycledInst.zone = 'deck';
        ps.deck.push(recycledId);
      }
    }
    if (count > 0) {
      ps.deck = shuffle(state.rng, ps.deck);
    }
    // Draw 1 card
    if (ps.deck.length > 0) {
      const { drawn, remaining } = drawCards(ps.deck, 1);
      ps.deck = remaining;
      ps.hand.push(...drawn);
      for (const id of drawn) {
        state.cardInstances.get(id)!.zone = 'hand';
        events.push({ type: 'card-drawn', instanceId: id, player: inst.owner });
      }
    }
    return events;
  },

  'shuffle-hand': (state, sourceId, _catalog, _intensity) => {
    const inst = getInstance(state, sourceId);
    const ps = getPlayerState(state, inst.owner);
    const events: GameEvent[] = [];
    const handSize = ps.hand.length;
    // Discard entire hand
    for (const handId of ps.hand) {
      const handInst = state.cardInstances.get(handId);
      if (handInst) {
        handInst.zone = 'discard';
        ps.discardPile.push(handId);
        events.push({ type: 'card-discarded', instanceId: handId, player: inst.owner });
      }
    }
    ps.hand = [];
    // Draw handSize + 1
    const drawCount = Math.min(handSize + 1, ps.deck.length);
    if (drawCount > 0) {
      const { drawn, remaining } = drawCards(ps.deck, drawCount);
      ps.deck = remaining;
      ps.hand.push(...drawn);
      for (const id of drawn) {
        state.cardInstances.get(id)!.zone = 'hand';
        events.push({ type: 'card-drawn', instanceId: id, player: inst.owner });
      }
    }
    return events;
  },

  'equalize': (state, sourceId, catalog, intensity) => {
    const events: GameEvent[] = [];
    for (const side of ['player', 'opponent'] as const) {
      const ps = getPlayerState(state, side);
      for (const slot of ps.ring) {
        if (!slot.instanceId) continue;
        const target = getInstance(state, slot.instanceId);
        const oldPower = target.currentPower;
        target.currentPower = intensity;
        if (oldPower !== intensity) {
          events.push({ type: 'power-changed', instanceId: slot.instanceId, oldPower, newPower: intensity, amount: intensity - oldPower });
        }
      }
    }
    return events;
  },

  'advance-clock': (state, _sourceId, _catalog, intensity) => {
    if (intensity <= 0) return [];
    const oldClock = state.clock;
    adjustClock(state, intensity);
    if (state.clock === oldClock) return [];
    return [{ type: 'clock-changed', clock: state.clock }];
  },

  'rewind-clock': (state, _sourceId, _catalog, intensity) => {
    if (intensity <= 0) return [];
    const oldClock = state.clock;
    adjustClock(state, -intensity);
    if (state.clock === oldClock) return [];
    return [{ type: 'clock-changed', clock: state.clock }];
  },

  // Passive effects — checked during power calculation, turn phases, or support, not triggered directly
  'passive-easy-support': () => [],
  'passive-hand-size': () => [],
  'passive-attack-ready': () => [],
  'passive-damage-exhausted': () => [],
  'passive-pierce': () => [],
  'passive-leech': () => [],
  'passive-cannot-block': () => [],
  'passive-disable-activated': () => [],
  'passive-disable-on-play': () => [],
  // Consulted inside koCharacter at the moment this character would die —
  // restores in-place with the configured power AND marks the instance
  // ability-disabled so it can't fire again. Handler stub because the
  // actual work happens at the death site, not at processAbility time.
  'passive-rebirth': () => [],
};

export function processAbility(
  state: GameState,
  sourceInstanceId: string,
  ability: Ability,
  catalog: Map<string, CardDef>,
): GameEvent[] {
  // Verb+target dispatch path: when an ability carries a target, its effect
  // is a Verb and we resolve the target selector, then apply the verb to
  // each character in the resolved list.
  if (ability.target) {
    if (!isVerb(ability.effect)) {
      console.warn(`Ability has target ${ability.target} but effect ${ability.effect} is not a Verb`);
      return [];
    }
    const verb = VERB_HANDLERS[ability.effect];
    const targets = selectTargets(state, sourceInstanceId, catalog, ability.target, ability.effect);
    const events: GameEvent[] = [];
    const intensity = ability.intensity ?? 0;
    for (const targetId of targets) {
      // Skip stale ids — a previous verb application this same resolve could
      // have KO'd an earlier target (e.g. destroy + all-characters after a
      // prior effect already removed one of them).
      if (!state.cardInstances.has(targetId)) continue;
      events.push(...verb(state, sourceInstanceId, targetId, catalog, intensity));
    }
    return events;
  }

  // Legacy atomic-slug path: player-surface effects, compound effects,
  // conditional effects, and passives still dispatch by effect name.
  const handler = EFFECT_HANDLERS[ability.effect];
  if (!handler) {
    console.warn(`Unknown effect: ${ability.effect}`);
    return [];
  }
  return handler(state, sourceInstanceId, catalog, ability.intensity ?? 0);
}

// ── Target Preview ─────────────────────────────────────────────────
//
// Parallel registry to EFFECT_HANDLERS: pure functions that return which
// cards/zones an effect *would* target if invoked, without mutating state.
// Used by the drag-to-play UX to highlight affected targets while an action
// card is being dragged toward the battle line.

export interface PreviewResult {
  targets: string[];           // ring instanceIds that will be affected
  hitsOpponentHp?: boolean;    // highlight the opponent's HP bar
  hitsSelfHp?: boolean;        // highlight the player's own HP bar
  hitsSelfDeck?: boolean;      // highlight the player's deck (draw)
  hitsSelfSpirit?: boolean;    // highlight the player's spirit counter
}

type PreviewHandler = (
  state: GameState,
  sourceInstanceId: string,
  catalog: Map<string, CardDef>,
  intensity: number,
) => PreviewResult;

const EFFECT_PREVIEWS: Record<string, PreviewHandler> = {
  'draw': () => ({ targets: [], hitsSelfDeck: true }),
  'heal': () => ({ targets: [], hitsSelfHp: true }),
  'grant-extra-rally': () => ({ targets: [] }),
  'grant-extra-support': () => ({ targets: [] }),
  'damage-opponent': () => ({ targets: [], hitsOpponentHp: true }),
};

/** Returns the cards/zones an action card would affect if played now. Pure. */
export function previewTargets(
  state: GameState,
  sourceInstanceId: string,
  catalog: Map<string, CardDef>,
): PreviewResult {
  const inst = getInstance(state, sourceInstanceId);
  const def = catalog.get(inst.cardId);
  if (!def || def.type !== 'action') return { targets: [] };

  // Verb+target preview: resolve via selector. For `slay`, filter down to
  // targets that actually meet the power threshold — the preview should show
  // what will be hit, not everything the selector considered.
  if (def.target && isVerb(def.effect)) {
    const intensity = def.intensity ?? 0;
    const all = selectTargets(state, sourceInstanceId, catalog, def.target, def.effect);
    if (def.effect === 'slay') {
      return {
        targets: all.filter(id => getEffectivePower(state, id, catalog) >= intensity),
      };
    }
    return { targets: all };
  }

  const handler = EFFECT_PREVIEWS[def.effect];
  if (!handler) return { targets: [] };
  return handler(state, sourceInstanceId, catalog, def.intensity ?? 0);
}

export function getPassivePowerModifiers(
  state: GameState,
  targetInstanceId: string,
  catalog: Map<string, CardDef>,
): number {
  let bonus = 0;
  const targetInst = getInstance(state, targetInstanceId);
  const ps = getPlayerState(state, targetInst.owner);

  // Bonuses from friendly passives. Skip instances whose ability has
  // been disabled (silence verb, future silencing effects) — the
  // passive shouldn't keep contributing once its slot is wiped.
  for (const slot of ps.ring) {
    if (!slot.instanceId) continue;
    const inst = getInstance(state, slot.instanceId);
    if (inst.abilityDisabled) continue;
    const def = catalog.get(inst.cardId);
    if (!def || def.type !== 'character' || def.ability?.type !== 'passive') continue;

    if (def.ability.effect === 'passive-boost-allies' && slot.instanceId !== targetInstanceId) {
      bonus += def.ability.intensity ?? 0;
    }
  }

  // Penalties from opponent passives (intimidate). Same disabled
  // guard so a silenced intimidator stops debuffing.
  const opSide = getOpponentSide(targetInst.owner);
  const ops = getPlayerState(state, opSide);
  for (const slot of ops.ring) {
    if (!slot.instanceId) continue;
    const inst = getInstance(state, slot.instanceId);
    if (inst.abilityDisabled) continue;
    const def = catalog.get(inst.cardId);
    if (!def || def.type !== 'character' || def.ability?.type !== 'passive') continue;

    if (def.ability.effect === 'passive-intimidate') {
      bonus -= def.ability.intensity ?? 0;
    }
  }

  return bonus;
}

export function getPassiveSupportBonus(
  state: GameState,
  side: PlayerSide,
  catalog: Map<string, CardDef>,
): number {
  let bonus = 0;
  const ps = getPlayerState(state, side);
  for (const slot of ps.ring) {
    if (!slot.instanceId) continue;
    const inst = getInstance(state, slot.instanceId);
    if (inst.abilityDisabled) continue;
    const def = catalog.get(inst.cardId);
    if (!def || def.type !== 'character' || def.ability?.type !== 'passive') continue;
    if (def.ability.effect === 'passive-support-bonus') {
      bonus += def.ability.intensity ?? 0;
    }
  }
  return bonus;
}

export function getRalliesPerTurn(
  state: GameState,
  side: PlayerSide,
  catalog: Map<string, CardDef>,
): number {
  const config = getConfig();
  const ps = getPlayerState(state, side);
  return config.baseRalliesPerTurn + ps.bonusRallies;
}

export function getSupportsPerTurn(
  state: GameState,
  side: PlayerSide,
  catalog: Map<string, CardDef>,
): number {
  const config = getConfig();
  const ps = getPlayerState(state, side);
  let passiveBonus = 0;
  for (const slot of ps.ring) {
    if (!slot.instanceId) continue;
    const inst = getInstance(state, slot.instanceId);
    if (inst.abilityDisabled) continue;
    const def = catalog.get(inst.cardId);
    if (!def || def.type !== 'character' || def.ability?.type !== 'passive') continue;
    if (def.ability.effect === 'passive-extra-support') {
      passiveBonus += def.ability.intensity ?? 0;
    }
  }
  return config.baseSupportsPerTurn + ps.bonusSupports + passiveBonus;
}

export function hasPassiveAttackReady(
  state: GameState,
  instanceId: string,
  catalog: Map<string, CardDef>,
): boolean {
  return hasPassive(state, instanceId, 'passive-attack-ready', catalog);
}

export function hasPassive(
  state: GameState,
  instanceId: string,
  effectKey: string,
  catalog: Map<string, CardDef>,
): boolean {
  const inst = getInstance(state, instanceId);
  // Reborn characters have their ability slot zeroed out via the
  // `abilityDisabled` flag — they retain their cardId so portraits +
  // names + stats remain identifiable, but no passive (boost-allies,
  // intimidate, pierce, leech, rebirth itself, ...) should fire for
  // them. Gate here so every passive-check site picks it up.
  if (inst.abilityDisabled) return false;
  const def = catalog.get(inst.cardId);
  return def?.type === 'character' && def.ability?.type === 'passive' && def.ability.effect === effectKey;
}

/** True if any character on either side's ring has the given passive effect.
 *  Used by global-lockdown passives (e.g. passive-disable-activated). */
export function hasAnyPassiveInRing(
  state: GameState,
  effectKey: string,
  catalog: Map<string, CardDef>,
): boolean {
  for (const side of ['player', 'opponent'] as const) {
    const ps = getPlayerState(state, side);
    for (const slot of ps.ring) {
      if (!slot.instanceId) continue;
      if (hasPassive(state, slot.instanceId, effectKey, catalog)) return true;
    }
  }
  return false;
}

export function processDamageFrozen(
  state: GameState,
  side: PlayerSide,
  catalog: Map<string, CardDef>,
): GameEvent[] {
  const events: GameEvent[] = [];
  const ps = getPlayerState(state, side);
  const opSide = getOpponentSide(side);
  const ops = getPlayerState(state, opSide);

  // Find all passive-damage-exhausted on this player's side
  let totalDamage = 0;
  const sourceIds: string[] = [];
  for (const slot of ps.ring) {
    if (!slot.instanceId) continue;
    const inst = getInstance(state, slot.instanceId);
    const def = catalog.get(inst.cardId);
    if (def?.type === 'character' && def.ability?.type === 'passive' && def.ability.effect === 'passive-damage-exhausted') {
      totalDamage += def.ability.intensity ?? 0;
      sourceIds.push(slot.instanceId);
    }
  }

  if (totalDamage <= 0) return events;

  // Collect exhausted enemy targets first. A repeated passive whose
  // condition isn't met this turn (no exhausted enemies) should not
  // banner — that's just noise the player has to skip.
  const targetIds: string[] = [];
  for (const slot of ops.ring) {
    if (!slot.instanceId) continue;
    const target = getInstance(state, slot.instanceId);
    if (target.isExhausted) targetIds.push(slot.instanceId);
  }
  if (targetIds.length === 0) return events;

  // Banner each source of the damage (one event per source character, not
  // per damaged target — multiple targets sharing one source produce one
  // banner per source).
  for (const id of sourceIds) {
    events.push({ type: 'ability-triggered', instanceId: id, abilityType: 'passive', effect: 'passive-damage-exhausted' });
  }

  // Deal damage to each exhausted enemy.
  for (const targetId of targetIds) {
    const target = getInstance(state, targetId);
    applyDamage(target, totalDamage);
    events.push({ type: 'combat-damage', attackerInstanceId: sourceIds[0] ?? '', defenderInstanceId: targetId, damage: totalDamage });
    if (getEffectivePower(state, targetId, catalog) <= 0) {
      koCharacter(state, targetId, events, catalog);
    }
  }

  return events;
}

// ── Trigger dispatch helpers ────────────────────────────────────────
// Fire abilities whose `type` is one of the new trigger categories
// (on-attack / on-attack-alone / on-damage-opponent / end-of-turn /
// beginning-of-turn). The dispatch path inside processAbility doesn't
// branch on type — these helpers just decide which characters' abilities
// fire at which moment, then hand off to the same dispatcher.

function fireTriggerForIds(
  state: GameState,
  ids: string[],
  triggerType: AbilityType,
  catalog: Map<string, CardDef>,
): GameEvent[] {
  const events: GameEvent[] = [];
  for (const id of ids) {
    const inst = state.cardInstances.get(id);
    // Skip KO'd characters (sent to discard mid-iteration). Combat triggers
    // run while attackers are in zone 'attackLine'; turn-cycle triggers run
    // while characters are in 'ring'. Anything not 'discard' is fair game.
    if (!inst || inst.zone === 'discard') continue;
    // Per-instance ability lockdown (silence verb, future silencing
    // effects). Without this, a silenced character's on-attack /
    // on-damage-opponent / on-play-character / on-play-action /
    // end-of-turn / beginning-of-turn trigger would still fire when
    // its trigger condition hits.
    if (inst.abilityDisabled) continue;
    const def = catalog.get(inst.cardId);
    if (def?.type !== 'character') continue;
    if (def.ability?.type !== triggerType) continue;
    // Resolve the ability FIRST so we can suppress the trigger banner
    // when the effect was a no-op (destroy / damage with no eligible
    // targets, heal at full HP, draw with empty deck, etc.). Without
    // this gate, the player sees an EOT banner play out — including
    // the settle delay in main.ts:applyDeferredPhaseEffects — for an
    // effect that didn't change anything. processAbility returns []
    // in all the no-target / no-effect cases; anything that mutates
    // state at least emits one engine event.
    const abilityEvents = processAbility(state, id, def.ability, catalog);
    if (abilityEvents.length === 0) continue;
    events.push({ type: 'ability-triggered', instanceId: id, abilityType: triggerType, effect: def.ability.effect });
    events.push(...abilityEvents);
    // Narrate what the trigger actually did. End-of-turn / on-attack /
    // on-damage-opponent / on-play-character / on-play-action / etc.
    // otherwise fire invisibly in the log.
    const narration = narrateAbilityEvents(abilityEvents, state, catalog);
    if (narration) state.log.push(`${logCardLink(def)}: ${narration}`);
  }
  return events;
}

function fireTriggerOnSide(
  state: GameState,
  side: PlayerSide,
  triggerType: AbilityType,
  catalog: Map<string, CardDef>,
): GameEvent[] {
  const ps = getPlayerState(state, side);
  const ids: string[] = [];
  for (const slot of ps.ring) {
    if (slot.instanceId) ids.push(slot.instanceId);
  }
  return fireTriggerForIds(state, ids, triggerType, catalog);
}

export function fireOnAttackTriggers(
  state: GameState,
  attackerIds: string[],
  catalog: Map<string, CardDef>,
): GameEvent[] {
  return fireTriggerForIds(state, attackerIds, 'on-attack', catalog);
}

export function fireOnAttackAloneTriggers(
  state: GameState,
  soleAttackerId: string,
  catalog: Map<string, CardDef>,
): GameEvent[] {
  return fireTriggerForIds(state, [soleAttackerId], 'on-attack-alone', catalog);
}

export function fireOnDamageOpponentTriggers(
  state: GameState,
  attackerIds: string[],
  hpDamage: number,
  catalog: Map<string, CardDef>,
): GameEvent[] {
  if (hpDamage <= 0) return [];
  // Suppressed by the same global lockdown as activated + on-attack
  // (passive-disable-activated reads "Characters can't use activated
  // (↩️) or combat (⚔️) abilities."). on-damage-opponent is the
  // last of the three ⚔️ triggers — gating it here keeps the
  // CombatResolver call sites clean.
  if (hasAnyPassiveInRing(state, 'passive-disable-activated', catalog)) return [];
  return fireTriggerForIds(state, attackerIds, 'on-damage-opponent', catalog);
}

export function processBeginningOfTurnTriggers(
  state: GameState,
  side: PlayerSide,
  catalog: Map<string, CardDef>,
): GameEvent[] {
  return fireTriggerOnSide(state, side, 'beginning-of-turn', catalog);
}

export function processEndOfTurnTriggers(
  state: GameState,
  side: PlayerSide,
  catalog: Map<string, CardDef>,
): GameEvent[] {
  return fireTriggerOnSide(state, side, 'end-of-turn', catalog);
}

// Fire on-play-character on the active player's *other* ring characters
// when a new character enters. The just-played character is excluded —
// the rules text reads "when you play another character", and a card
// shouldn't react to its own arrival.
export function fireOnPlayCharacterTriggers(
  state: GameState,
  playedInstanceId: string,
  catalog: Map<string, CardDef>,
): GameEvent[] {
  const ps = getPlayerState(state, state.currentPlayer);
  const ids: string[] = [];
  for (const slot of ps.ring) {
    if (slot.instanceId && slot.instanceId !== playedInstanceId) {
      ids.push(slot.instanceId);
    }
  }
  return fireTriggerForIds(state, ids, 'on-play-character', catalog);
}

// Fire on-play-action on `side`'s ring. Takes side as a parameter because
// PLAY_REACTION is played by the non-current player during the counter
// phase, so we can't infer the right ring from state.currentPlayer alone.
export function fireOnPlayActionTriggers(
  state: GameState,
  side: PlayerSide,
  catalog: Map<string, CardDef>,
): GameEvent[] {
  return fireTriggerOnSide(state, side, 'on-play-action', catalog);
}

export function getMaxHandSize(
  state: GameState,
  side: PlayerSide,
  catalog: Map<string, CardDef>,
): number {
  const config = getConfig();
  let bonus = 0;
  const ps = getPlayerState(state, side);
  for (const slot of ps.ring) {
    if (!slot.instanceId) continue;
    const inst = getInstance(state, slot.instanceId);
    if (inst.abilityDisabled) continue;
    const def = catalog.get(inst.cardId);
    if (!def || def.type !== 'character' || def.ability?.type !== 'passive') continue;
    if (def.ability.effect === 'passive-hand-size') {
      bonus += def.ability.intensity ?? 0;
    }
  }
  return Math.max(1, config.maxHandSize + bonus);
}

export { koCharacter };
